import api from '../httpClient';

export type ProgrammeDeliveryMode = 'online' | 'in_person' | 'both';
export type ProgrammePricingType =
  | 'total_programme_price'
  | 'price_per_session'
  | 'free';

export interface NutritionistProgramme {
  id: string;
  name: string;
  description: string;
  type: string;
  pricingType: ProgrammePricingType;
  numberOfSessions: number;
  sessionDurationMinutes: number;
  priceAmount: number;
  rate: number;
  totalPrice: number;
  pricePerSession: number;
  deliveryMode: ProgrammeDeliveryMode;
  isActive: boolean;
  raw?: Record<string, unknown>;
}

export interface NutritionistPlan {
  id: string;
  name: string;
  description: string;
  total?: number | string | null;
  programmes: NutritionistProgramme[];
  raw?: Record<string, unknown>;
}

export interface CreateNutritionistPlanPayload {
  name: string;
  description: string;
}

export type UpdateNutritionistPlanPayload = CreateNutritionistPlanPayload;

export interface CreateNutritionistProgrammePayload {
  name: string;
  description: string;
  type: string;
  pricingType?: ProgrammePricingType;
  numberOfSessions: number;
  sessionsIncluded?: number;
  sessionDurationMinutes: number;
  durationPerSessionMinutes?: number;
  rate: number;
  priceAmount?: number;
  totalPrice?: number;
  pricePerSession?: number;
  amountPerSession?: number;
  deliveryMode: ProgrammeDeliveryMode;
  isActive: boolean;
}

export type UpdateNutritionistProgrammePayload = CreateNutritionistProgrammePayload;

type NutritionistProgrammeApiPayload = {
  name: string;
  description: string;
  type: string;
  numberOfSessions: number;
  sessionDurationMinutes: number;
  pricingType: ProgrammePricingType;
  priceAmount: number;
  deliveryMode: ProgrammeDeliveryMode;
  isActive: boolean;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const unwrapApiData = (value: unknown): unknown => {
  const record = asRecord(value);
  if ('data' in record) {
    return unwrapApiData(record.data);
  }
  return value;
};

const readString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return '';
};

const readNumber = (...values: unknown[]): number => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value.replace(/[^0-9.-]/g, ''));
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return 0;
};

const readBoolean = (...values: unknown[]): boolean => {
  for (const value of values) {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === 'active') {
        return true;
      }
      if (normalized === 'false' || normalized === 'inactive') {
        return false;
      }
    }
  }
  return true;
};

const normalizeDeliveryMode = (value: unknown): ProgrammeDeliveryMode => {
  const normalized = String(value || 'both').trim().toLowerCase().replace(/[-\s]+/g, '_');
  if (normalized === 'online') {
    return 'online';
  }
  if (normalized === 'in_person' || normalized === 'offline' || normalized === 'inperson') {
    return 'in_person';
  }
  return 'both';
};

const normalizePricingType = (value: unknown): ProgrammePricingType => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, '_');

  if (
    normalized === 'price_per_session' ||
    normalized === 'per_session' ||
    normalized === 'session_price' ||
    normalized === 'per_session_price'
  ) {
    return 'price_per_session';
  }

  if (normalized === 'free') {
    return 'free';
  }

  return 'total_programme_price';
};

const normalizeProgrammeTypeForApi = (value: unknown): string => {
  const raw = readString(value);
  const normalized = raw.toLowerCase().replace(/[-_\s]+/g, ' ');

  if (normalized === 'consultation' || normalized === 'free consultation') {
    return 'Consultation';
  }

  if (normalized === 'package') {
    return 'Package';
  }

  if (normalized === 'programme' || normalized === 'program' || normalized === 'fixed duration') {
    return 'Programme';
  }

  return raw || 'Programme';
};

const buildProgrammeApiPayload = (
  payload: CreateNutritionistProgrammePayload,
): NutritionistProgrammeApiPayload => {
  const pricingType = normalizePricingType(payload.pricingType);
  const priceAmount =
    pricingType === 'free'
      ? 0
      : pricingType === 'price_per_session'
        ? readNumber(payload.priceAmount, payload.amountPerSession, payload.pricePerSession, payload.rate)
        : readNumber(payload.priceAmount, payload.totalPrice, payload.rate);

  return {
    name: payload.name.trim(),
    description: payload.description.trim(),
    type: normalizeProgrammeTypeForApi(payload.type),
    numberOfSessions: readNumber(payload.numberOfSessions, payload.sessionsIncluded),
    sessionDurationMinutes: readNumber(payload.sessionDurationMinutes, payload.durationPerSessionMinutes),
    pricingType,
    priceAmount,
    deliveryMode: normalizeDeliveryMode(payload.deliveryMode),
    isActive: payload.isActive,
  };
};

const extractArray = (value: unknown, keys: string[]): unknown[] => {
  const payload = unwrapApiData(value);
  if (Array.isArray(payload)) {
    return payload;
  }

  const record = asRecord(payload);
  for (const key of keys) {
    const nested = record[key];
    if (Array.isArray(nested)) {
      return nested;
    }
  }

  return [];
};

const extractEntity = (value: unknown, keys: string[]): unknown => {
  const payload = unwrapApiData(value);
  const record = asRecord(payload);
  for (const key of keys) {
    if (record[key]) {
      return record[key];
    }
  }
  return payload;
};

export const normalizeNutritionistProgramme = (value: unknown): NutritionistProgramme => {
  const record = asRecord(value);
  const pricingType = normalizePricingType(record.pricingType ?? record.pricing_type ?? record.priceType);
  const numberOfSessions = readNumber(
    record.numberOfSessions,
    record.number_of_sessions,
    record.sessionsIncluded,
    record.sessions_included,
    record.sessions,
    record.sessionCount,
    record.session_count,
  );
  const explicitTotalPrice =
    pricingType === 'price_per_session'
      ? readNumber(
          record.totalPrice,
          record.total_price,
          record.totalProgrammePrice,
          record.total_programme_price,
          record.programmePrice,
          record.programme_price,
          record.total,
        )
      : readNumber(
          record.totalPrice,
          record.total_price,
          record.totalProgrammePrice,
          record.total_programme_price,
          record.programmePrice,
          record.programme_price,
          record.rate,
          record.price,
          record.total,
          record.amount,
        );
  const priceAmount = readNumber(record.priceAmount, record.price_amount, record.price, record.amount, record.rate);
  const pricePerSession =
    readNumber(
      record.pricePerSession,
      record.price_per_session,
      record.amountPerSession,
      record.amount_per_session,
      record.sessionPrice,
      record.session_price,
      record.sessionRate,
      record.session_rate,
      record.ratePerSession,
      record.rate_per_session,
      record.perSessionRate,
      record.per_session_rate,
    ) || (pricingType === 'price_per_session' ? priceAmount : 0);
  const totalPrice =
    pricingType === 'free'
      ? 0
      : pricingType === 'price_per_session'
        ? explicitTotalPrice || (numberOfSessions > 0 ? numberOfSessions * pricePerSession : pricePerSession)
        : explicitTotalPrice || priceAmount;

  return {
    id: readString(record.id, record._id, record.programmeId, record.programme_id, record.programId, record.program_id),
    name: readString(record.name, record.title),
    description: readString(record.description),
    type: readString(record.type, record.programmeType, record.programme_type, record.programType, record.program_type) || 'fixed_duration',
    pricingType,
    numberOfSessions,
    sessionDurationMinutes: readNumber(
      record.sessionDurationMinutes,
      record.session_duration_minutes,
      record.durationPerSessionMinutes,
      record.duration_per_session_minutes,
      record.durationPerSession,
      record.duration_per_session,
      record.sessionDuration,
      record.session_duration,
      record.durationMinutes,
      record.duration_minutes,
      record.duration,
    ),
    priceAmount: pricingType === 'price_per_session' ? pricePerSession : totalPrice,
    rate: totalPrice,
    totalPrice,
    pricePerSession,
    deliveryMode: normalizeDeliveryMode(record.deliveryMode ?? record.delivery_mode ?? record.mode),
    isActive: readBoolean(record.isActive, record.active, record.status),
    raw: record,
  };
};

export const normalizeNutritionistPlan = (value: unknown): NutritionistPlan => {
  const record = asRecord(value);
  const programmes = extractArray(record.programmes ?? record.programs ?? record.packages ?? [], [
    'programmes',
    'programs',
    'packages',
  ]).map(normalizeNutritionistProgramme);

  return {
    id: readString(record.id, record._id, record.planId),
    name: readString(record.name, record.title),
    description: readString(record.description),
    total: (record.total ?? record.totalAmount ?? record.amount ?? null) as number | string | null,
    programmes,
    raw: record,
  };
};

export const getNutritionistPlans = async (): Promise<NutritionistPlan[]> => {
  const response = await api.get('/nutritionists/plans');
  return extractArray(response.data, ['plans', 'items', 'results']).map(normalizeNutritionistPlan);
};

export const createNutritionistPlan = async (
  payload: CreateNutritionistPlanPayload,
): Promise<NutritionistPlan> => {
  const response = await api.post('/nutritionists/plans', payload);
  return normalizeNutritionistPlan(extractEntity(response.data, ['plan']));
};

export const updateNutritionistPlan = async (
  planId: string,
  payload: UpdateNutritionistPlanPayload,
): Promise<NutritionistPlan> => {
  const response = await api.put(`/nutritionists/plans/${encodeURIComponent(planId)}`, payload);
  return normalizeNutritionistPlan(extractEntity(response.data, ['plan']));
};

export const deleteNutritionistPlan = async (planId: string): Promise<void> => {
  await api.delete(`/nutritionists/plans/${encodeURIComponent(planId)}`);
};

export const createNutritionistProgramme = async (
  planId: string,
  payload: CreateNutritionistProgrammePayload,
): Promise<NutritionistProgramme> => {
  const response = await api.post(
    `/nutritionists/plans/${encodeURIComponent(planId)}/programmes`,
    buildProgrammeApiPayload(payload),
  );
  return normalizeNutritionistProgramme(extractEntity(response.data, ['programme', 'program']));
};

export const updateNutritionistProgramme = async (
  planId: string,
  programmeId: string,
  payload: UpdateNutritionistProgrammePayload,
): Promise<NutritionistProgramme> => {
  const response = await api.put(
    `/nutritionists/plans/${encodeURIComponent(planId)}/programmes/${encodeURIComponent(programmeId)}`,
    buildProgrammeApiPayload(payload),
  );
  return normalizeNutritionistProgramme(extractEntity(response.data, ['programme', 'program']));
};

export const deleteNutritionistProgramme = async (
  planId: string,
  programmeId: string,
): Promise<void> => {
  await api.delete(
    `/nutritionists/plans/${encodeURIComponent(planId)}/programmes/${encodeURIComponent(programmeId)}`,
  );
};
